import { store } from '@redux/store';
import { fromEvent, map, withLatestFrom } from 'rxjs';
import { getAnalyticsTrackingProperties } from 'features/trackingProperties';
import { segmentTrack, segmentIdentify } from 'features/segment';
import {
  getStoredUserData,
  ModalStep,
  selectModal$,
  getSegmentEventProperties
} from 'features/selfGuidedTour';
import {
  mapInteractionOccurred$,
  panelUpdatedOccurred$,
  panelScrollOccurred$,
  drawerDraggedOccurred$,
  lotSelectedOccurred$,
  cardsScrolledOccurred$,
  tabSelectedOccurred$,
  notifyWhenAvailableClickedOccurred$,
  floorPlanViewedOccurred$,
  viewFloorPlanClickedOccurred$,
  emptyConfigViewedOccurred$,
  similarHomesToggledOccurred$,
  similarHomeCardClickedOccurred$,
  soldSeeAllClickedOccurred$,
  detailViewOpenedOccurred$,
  detailsTabSelectedOccurred$,
  changeLotSelectedOccurred$,
  exteriorSelectedOccurred$,
  exteriorSelectorExpandedOccurred$,
  mobileLotMapClickedOccurred$,
  mobileLotCardNavigationOccurred$,
  buildSegmentMapInteractionEvent,
  buildSegmentPanelUpdatedEvent,
  buildSegmentPanelScrollEvent,
  buildSegmentDrawerDraggedEvent,
  buildSegmentLotSelectedEvent,
  buildSegmentCardsScrolledEvent,
  buildSegmentTabSelectedEvent,
  buildSegmentNotifyWhenAvailableClickedEvent,
  buildSegmentFloorPlanViewedEvent,
  buildSegmentViewFloorPlanClickedEvent,
  buildSegmentEmptyConfigViewedEvent,
  buildSegmentSimilarHomesToggledEvent,
  buildSegmentSimilarHomeCardClickedEvent,
  buildSegmentSoldSeeAllClickedEvent,
  buildSegmentDetailViewOpenedEvent,
  buildSegmentDetailsTabSelectedEvent,
  buildSegmentChangeLotSelectedEvent,
  buildSegmentExteriorSelectedEvent,
  buildSegmentExteriorSelectorExpandedEvent,
  buildSegmentMobileLotMapClickedEvent,
  buildSegmentMobileLotCardNavigationEvent,
} from 'features/nativeInteractiveSiteMap';
import {
  leadOptionSelectedOccurred$,
  leadFormSubmittedOccurred$,
  leadFormClosedOccurred$,
  leadFormAbandonedOccurred$,
  leadFormSubmissionFailedOccurred$,
  buildSegmentLeadButtonClickedEvent,
  buildSegmentLeadModalFormSubmittedEvent,
  buildSegmentLeadModalClosedEvent,
  buildSegmentLeadFormAbandonedEvent,
  buildSegmentLeadFormSubmissionFailedEvent,
  notifySubmitClickedOccurred$,
  notifySubmissionFailedOccurred$,
  notifyModalClosedOccurred$,
  notifyAbandonedOccurred$,
  buildSegmentNotifySubmitClickedEvent,
  buildSegmentNotifySubmissionFailedEvent,
  buildSegmentNotifyModalClosedEvent,
  buildSegmentNotifyAbandonedEvent,
} from 'features/leadGenerators';
import withPosition from '../../services/contactBuilderPosition';

const uTourDialogElement = document.querySelector(
  '[data-self-guided-tour-dialog]'
);
const uTourCtasButtons = document.querySelectorAll(
  '[data-self-guided-tour-trigger]'
);

/**
 * @function mapGlobalPropertiesToSegment
 * @description Maps global properties to a Segment tracking object.
 * @returns {Object} An object containing the mapped properties for Segment tracking.
 */
function mapGlobalPropertiesToSegment() {
  const props = getAnalyticsTrackingProperties();
  return {
    brand_partner_name: props.brand_partner_name,
    builder_id: props.builder_id,
    builder_name: props.builder_name,
    city: props.city,
    community_id: props.communityId,
    community_name: props.community_name,
    communitytype: props.communityType,
    home_price: props.home_price,
    home_type: props.home_type,
    is_spotlight: props.is_spotlight,
    market_id: props.marketId,
    market_name: props.marketName,
    page_type: props.page_type,
    partner_id: props.partner_id,
    partner_name: props.partner_name,
    primary_image_url: props.primary_image_url,
    position: props.result_position,
    response_version: props.response_version,
    state: props.state,
    zip: props.zip
  };
}

/**
 * @function getSharedUtourProperties
 * @description Gathers and returns shared analytics properties for self-guided tour events.
 *              Combines user data, analytics tracking properties, and uTour data into a single object
 *              for use in GA4 event tracking.
 * @returns {Object} An object containing shared analytics properties for self-guided tour events.
 */
function getSharedUtourProperties() {
  // Check if the utour data is available.
  const user = getStoredUserData();
  const analyticsProperties = mapGlobalPropertiesToSegment();

  analyticsProperties.email = user.email || undefined;
  analyticsProperties.first_name = user.firstName || undefined;
  analyticsProperties.last_name = user.lastName || undefined;
  analyticsProperties.phone = user.phoneNumber || undefined;

  return analyticsProperties;
}

/**
 * @function sendUtourEvent
 * @description Dispatches a GA4 analytics event for self-guided tour interactions.
 *              Combines shared and custom properties, then sends the event to the Redux store.
 * @param {Array} data - Array with events obj properties and event name.
 */
const sendUtourEvent = events => {
  if (!events) {
    return;
  }

  const eventsArray = Array.isArray(events) ? events : [events];
  if (eventsArray.length > 0) {
    eventsArray.forEach(item => {
      const sharedProperties = getSharedUtourProperties();
      const customProperties = {
        tour_type: 'utour',
        ...sharedProperties,
        ...item.data
      };
      store.dispatch(
        segmentTrack({ eventName: item.eventName, customProperties })
      );
    });
  }
};

/**
 * @function triggerBannerButtonClick
 * @description Handles the click event on the self-guide utour button and dispatches a tracking event to Segment.
 * @param {Event} event - The HTML event object.
 */

const triggerBannerButtonClick = function (event) {
  const sectionName = event.target.closest('[data-your-self-guided-home-tour]')
    ? 'Your self-guided home tour'
    : 'Request a tour today!';

  const customProperties = {
    button_action: 'ScheduleSelfTour',
    section_name: sectionName
  };

  sendUtourEvent({ eventName: 'Lead Button Clicked', data: customProperties });
};

// Observable to handle click on self-guides banner utour buttons.
uTourCtasButtons.forEach(buttonElement =>
  fromEvent(buttonElement, 'click').subscribe(triggerBannerButtonClick)
);

function sendLoginRegisterEvents({ currentStep, previousStep }) {
  if (
    currentStep === ModalStep.SignIn.toString() ||
    currentStep === ModalStep.SignUp.toString()
  ) {
    return;
  }

  if (
    previousStep === ModalStep.SignIn.toString() ||
    previousStep === ModalStep.SignUp.toString()
  ) {
    const user = getStoredUserData();
    const eventName =
      previousStep === ModalStep.SignIn.toString() ? 'Sign in' : 'Sign up';

    store.dispatch(
      segmentIdentify({
        eventName,
        customProperties: user
      })
    );

    if (previousStep === ModalStep.SignUp.toString()) {
      sendUtourEvent({ eventName: 'Register' });
    }
  }
}

selectModal$.subscribe(sendLoginRegisterEvents);

/**
 * Sets up an observable that listens for click events on the self-guided tour dialog element.
 * Each click event is combined with the latest modal step context, mapped to analytics event properties,
 * and then dispatched using sendUtourEvent for tracking.
 */
if (uTourDialogElement) {
  fromEvent(uTourDialogElement, 'click')
    .pipe(
      withLatestFrom(selectModal$),
      map(([event, { currentStep, previousStep }]) => ({
        event,
        currentStep,
        previousStep
      })),
      map(getSegmentEventProperties)
    )
    .subscribe(sendUtourEvent);
}

/**
 * @function mapGlobalPropertiesToNativeInteractiveSiteMapSegment
 * @description Global properties merged into every Native ISM Segment event — a subset of the
 * field list mapGlobalPropertiesToSegment above uses (only the ones Native ISM's tables ask for).
 * @returns {Object}
 */
function mapGlobalPropertiesToNativeInteractiveSiteMapSegment() {
  const props = getAnalyticsTrackingProperties();
  return {
    community_id: props.communityId,
    community_name: props.community_name,
    builder_id: props.builder_id,
    builder_name: props.builder_name,
    market_id: props.marketId,
    market_name: props.marketName,
    state: props.state,
    city: props.city,
  };
}

/**
 * @function dispatchNativeInteractiveSiteMapSegmentEvent
 * @description Merges global properties into a built Native ISM event and dispatches it via
 * segmentTrack. The ISM feature has no analytics knowledge of its own; it only emits raw
 * payloads on the Subjects below (see features/nativeInteractiveSiteMap/redux/observables.js).
 * @param {{ eventName: string, customProperties: Object }} builtEvent
 */
function dispatchNativeInteractiveSiteMapSegmentEvent({ eventName, customProperties }) {
  store.dispatch(
    segmentTrack({
      eventName,
      customProperties: { ...mapGlobalPropertiesToNativeInteractiveSiteMapSegment(), ...customProperties }
    })
  );
}

mapInteractionOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentMapInteractionEvent(payload))
);
panelUpdatedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentPanelUpdatedEvent(payload))
);
panelScrollOccurred$.subscribe(() =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentPanelScrollEvent())
);
drawerDraggedOccurred$.subscribe(() =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentDrawerDraggedEvent())
);
lotSelectedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentLotSelectedEvent(payload))
);
cardsScrolledOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentCardsScrolledEvent(payload))
);
tabSelectedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentTabSelectedEvent(payload))
);
notifyWhenAvailableClickedOccurred$.subscribe(() =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentNotifyWhenAvailableClickedEvent())
);
floorPlanViewedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentFloorPlanViewedEvent(payload))
);
viewFloorPlanClickedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentViewFloorPlanClickedEvent(payload))
);
emptyConfigViewedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentEmptyConfigViewedEvent(payload))
);
similarHomesToggledOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentSimilarHomesToggledEvent(payload))
);
similarHomeCardClickedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentSimilarHomeCardClickedEvent(payload))
);
soldSeeAllClickedOccurred$.subscribe(() =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentSoldSeeAllClickedEvent())
);

/**
 * @function dispatchLeadFormSegmentEvent
 * @description Dispatches a built Contact Builder lead-form event via segmentTrack. The
 * leadGenerators feature has no analytics knowledge of its own — it only emits raw payloads
 * (carrying `fromPage`, not `position`) on the Subjects below; withPosition (see
 * ../../services/contactBuilderPosition.js) translates that into this page's `position` value before the
 * payload reaches a build*Event function, since leadGenerators is shared and has no business
 * knowing what "ISM" means. Unlike the Native ISM events above, these carry no page-level global
 * properties — the ticket's (NHS20-8253) property list is fully self-contained per event.
 * @param {{ eventName: string, customProperties: Object }} builtEvent
 */
function dispatchLeadFormSegmentEvent({ eventName, customProperties }) {
  store.dispatch(segmentTrack({ eventName, customProperties }));
}

leadOptionSelectedOccurred$.subscribe((payload) =>
  dispatchLeadFormSegmentEvent(buildSegmentLeadButtonClickedEvent(withPosition(payload))),
);
leadFormSubmittedOccurred$.subscribe((payload) =>
  dispatchLeadFormSegmentEvent(buildSegmentLeadModalFormSubmittedEvent(withPosition(payload))),
);
leadFormClosedOccurred$.subscribe((payload) =>
  dispatchLeadFormSegmentEvent(buildSegmentLeadModalClosedEvent(withPosition(payload))),
);
leadFormAbandonedOccurred$.subscribe((payload) =>
  dispatchLeadFormSegmentEvent(buildSegmentLeadFormAbandonedEvent(withPosition(payload))),
);
leadFormSubmissionFailedOccurred$.subscribe((payload) =>
  dispatchLeadFormSegmentEvent(buildSegmentLeadFormSubmissionFailedEvent(withPosition(payload))),
);
detailViewOpenedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentDetailViewOpenedEvent(payload))
);
detailsTabSelectedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentDetailsTabSelectedEvent(payload))
);
changeLotSelectedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentChangeLotSelectedEvent(payload))
);
exteriorSelectedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentExteriorSelectedEvent(payload))
);
exteriorSelectorExpandedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentExteriorSelectorExpandedEvent(payload))
);
mobileLotMapClickedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentMobileLotMapClickedEvent(payload))
);
mobileLotCardNavigationOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentMobileLotCardNavigationEvent(payload))
);

/**
 * "Get on the list" (Coming Soon "Get Notified") events (NHS20-8254). Unlike the Contact Builder
 * events above, these carry no fromPage/position — the ticket's own event vocabulary (bespoke
 * "Native Map Notify *" names) is already ISM-flavored, so they're dispatched through the same
 * global-property-merging helper every other native ISM sibling on this page uses, for
 * consistency with those.
 */
notifySubmitClickedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentNotifySubmitClickedEvent(payload))
);
notifySubmissionFailedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentNotifySubmissionFailedEvent(payload))
);
notifyModalClosedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentNotifyModalClosedEvent(payload))
);
notifyAbandonedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapSegmentEvent(buildSegmentNotifyAbandonedEvent(payload))
);